import { useQuery } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'

interface AnalyticsData {
  total_views: number
  period_views: number
  top_countries: Array<{ name: string; count: number }>
  top_devices: Array<{ name: string; count: number }>
  top_referrers: Array<{ name: string; count: number }>
  link_clicks: Array<{ id: string; title: string; click_count: number }>
  product_clicks: Array<{ id: string; product_name: string; click_count: number }>
  views_by_day: Array<{ date: string; count: number }>
}

interface Props {
  pageId: string
}

export function BioAnalytics({ pageId }: Props) {
  const { data, isLoading } = useQuery<{ success: boolean; data: AnalyticsData }>({
    queryKey: ['bio-analytics', pageId],
    queryFn: () => apiRequest(`/api/bio-pages/${pageId}/analytics?days=30`),
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 bg-gray-100 dark:bg-white/5 rounded-lg animate-pulse" />
        ))}
      </div>
    )
  }

  const analytics = data?.data
  if (!analytics) {
    return <div className="text-sm text-gray-400">No analytics data available.</div>
  }

  const totalLinkClicks = analytics.link_clicks.reduce((sum, l) => sum + l.click_count, 0)
  const totalProductClicks = (analytics.product_clicks || []).reduce((sum, p) => sum + p.click_count, 0)
  const totalClicks = totalLinkClicks + totalProductClicks
  const ctr = analytics.total_views > 0 ? ((totalClicks / analytics.total_views) * 100).toFixed(1) : '0'

  return (
    <div className="space-y-6">
      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-50 dark:bg-[#0d121b] rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.total_views.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Total Views</p>
        </div>
        <div className="bg-gray-50 dark:bg-[#0d121b] rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalClicks.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Total Clicks</p>
        </div>
        <div className="bg-gray-50 dark:bg-[#0d121b] rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{ctr}%</p>
          <p className="text-xs text-gray-500">CTR</p>
        </div>
      </div>

      {/* Views chart (simple bar chart) */}
      {analytics.views_by_day.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Views (last 30 days)</h4>
          <div className="flex items-end gap-1 h-24">
            {analytics.views_by_day.slice(-30).map((day, i) => {
              const maxCount = Math.max(...analytics.views_by_day.map(d => d.count), 1)
              const height = (day.count / maxCount) * 100
              return (
                <div
                  key={i}
                  className="flex-1 bg-[#1754cf] rounded-t-sm min-w-[3px] hover:bg-[#1243a8] transition-colors"
                  style={{ height: `${Math.max(height, 2)}%` }}
                  title={`${day.date}: ${day.count} views`}
                />
              )
            })}
          </div>
        </div>
      )}

      {/* Link performance */}
      {analytics.link_clicks.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Link Performance</h4>
          <div className="space-y-2">
            {analytics.link_clicks.map(link => {
              const linkCtr = analytics.total_views > 0 ? ((link.click_count / analytics.total_views) * 100).toFixed(1) : '0'
              return (
                <div key={link.id} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">{link.title}</span>
                  <div className="flex items-center gap-3 shrink-0 ml-3">
                    <span className="text-xs text-gray-400">{link.click_count} clicks</span>
                    <span className="text-xs font-medium text-[#1754cf]">{linkCtr}% CTR</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Product performance */}
      {analytics.product_clicks && analytics.product_clicks.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Product Performance</h4>
          <div className="space-y-2">
            {analytics.product_clicks.map(product => {
              const productCtr = analytics.total_views > 0 ? ((product.click_count / analytics.total_views) * 100).toFixed(1) : '0'
              return (
                <div key={product.id} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">{product.product_name}</span>
                  <div className="flex items-center gap-3 shrink-0 ml-3">
                    <span className="text-xs text-gray-400">{product.click_count} clicks</span>
                    <span className="text-xs font-medium text-[#1754cf]">{productCtr}% CTR</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Top countries/devices */}
      <div className="grid grid-cols-2 gap-4">
        {analytics.top_countries.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase">Top Countries</h4>
            {analytics.top_countries.map(c => (
              <div key={c.name} className="flex justify-between text-xs py-1">
                <span className="text-gray-700 dark:text-gray-300">{c.name}</span>
                <span className="text-gray-400">{c.count}</span>
              </div>
            ))}
          </div>
        )}
        {analytics.top_devices.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase">Devices</h4>
            {analytics.top_devices.map(d => (
              <div key={d.name} className="flex justify-between text-xs py-1">
                <span className="text-gray-700 dark:text-gray-300 capitalize">{d.name}</span>
                <span className="text-gray-400">{d.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
